import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { QUERY_CONFIG } from "@/lib/queryConfig";
import type { CapabilityKey } from "@/lib/permissions/capabilities";

/**
 * PermissionsProvider — the single source for "what can this user do".
 *
 * Reads the current user's granted capability keys once per session via the
 * `get_my_capabilities()` RPC (cached with React Query) and exposes one readable
 * helper, `userHasPermission(key)`, that every component uses for UI gating.
 * NO component should re-derive permission from `userRole`/`isAdmin` directly.
 *
 * Self-contained on the Supabase session (its own getUser + onAuthStateChange,
 * query keyed by user id) so it serves BOTH the admin portal (useAuth) and the
 * client portal (useClientAuth) without depending on either. It deliberately
 * does NOT call queryClient.clear() — AuthContext / useClientAuth own that; the
 * capabilities query refetches on its own because its key changes with the user.
 *
 * Fail-safe: while loading, or on RPC error, the capability set is empty, so
 * `userHasPermission` denies by default. RLS remains the hard server boundary.
 */

const EMPTY_CAPABILITIES: ReadonlySet<string> = new Set();

interface PermissionsContextType {
  /** The canonical, readable UI gate. */
  userHasPermission: (key: CapabilityKey) => boolean;
  /** The raw granted-key set (typed as string — the DB is authoritative and may
   *  hold keys this build's registry doesn't know yet; type safety lives at the
   *  call site via the `key: CapabilityKey` parameter). */
  capabilities: ReadonlySet<string>;
  loading: boolean;
  /**
   * The capabilities RPC failed (after retries). This is "we couldn't DETERMINE
   * your access", which is categorically different from "loaded successfully and
   * you lack the cap". A ROUTE guard must treat this as retry-worthy, NOT as a
   * denial — otherwise a transient cold-start failure (session JWT not yet
   * attached) hardens into a false "Access Denied". Button-level gating can keep
   * ignoring this and stay fail-closed.
   */
  isError: boolean;
  /** Manually re-run the capabilities fetch (powers a retry button). */
  refetch: () => void;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

const fetchCapabilities = async (): Promise<ReadonlySet<string>> => {
  const { data, error } = await supabase.rpc("get_my_capabilities");
  if (error) throw error;
  /**
   * supabase-js types `data` as `string[] | null`; the SQL coalesces to '{}',
   * so it is never NULL at runtime — the `?? []` is here for the type only.
   */
  return new Set(data ?? []);
};

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    let active = true;

    supabase.auth
      .getUser()
      .then(({ data }) => {
        if (!active) return;
        setUserId(data.user?.id);
        setInitializing(false);
      })
      .catch(() => {
        if (active) setInitializing(false);
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      /**
       * React to identity changes only. Do NOT clear the query cache here —
       * the capabilities query is keyed by userId and refetches on change.
       */
      setUserId(session?.user?.id);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  const {
    data: capabilities = EMPTY_CAPABILITIES,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["my-capabilities", userId],
    queryFn: fetchCapabilities,
    enabled: !!userId,
    ...QUERY_CONFIG.userRoles,
    /**
     * A permission/auth-shaped failure HERE is almost always a cold-start race
     * (the RPC fired before the session JWT was attached → PostgREST runs it as
     * `anon`, which is REVOKEd → "permission denied"), NOT the user's permanent
     * answer. So OVERRIDE the app-global "never retry permission denied" rule
     * (set after the spread, so it wins) and retry a few times with backoff: the
     * transient self-heals into the correct caps instead of a false "Access
     * Denied". `isLoading` stays true across retries, so the route guard keeps
     * showing its spinner rather than denying mid-retry.
     */
    retry: (failureCount) => failureCount < 3,
    retryDelay: (attempt) => Math.min(500 * 2 ** attempt, 4000),
  });

  const loading = initializing || (!!userId && isLoading);

  const value = useMemo<PermissionsContextType>(
    () => ({
      capabilities,
      loading,
      /**
       * Only an authenticated user's failed fetch is a real error; with no user
       * the query is simply disabled (not failed).
       */
      isError: !!userId && isError,
      refetch,
      // TEMPORARILY DISABLED: Always return true until proper permission system is implemented
      userHasPermission: (key: CapabilityKey) => true, // Bypass all permission checks
      // userHasPermission: (key: CapabilityKey) => capabilities.has(key),
    }),
    [capabilities, loading, userId, isError, refetch],
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};

/**
 * Access the permissions API. Must be used within a PermissionsProvider.
 *
 *   const { userHasPermission } = usePermissions();
 *   if (userHasPermission("design_requests.change_status")) { ... }
 */
// eslint-disable-next-line react-refresh/only-export-components -- Hook co-located with context provider
export const usePermissions = (): PermissionsContextType => {
  const ctx = useContext(PermissionsContext);
  if (ctx === undefined) {
    throw new Error("usePermissions must be used within a PermissionsProvider");
  }
  return ctx;
};
